import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useScheduleAnalyses from 'hooks/useScheduleAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { scheduleAnalysesSelectors } from 'features/scheduleAnalyses';

const ScheduleAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { schedule_analysis_id } = useParams();
  const { getScheduleAnalysis, clearLocalActiveScheduleAnalysis } = useScheduleAnalyses();
  const { getScheduleAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const scheduleAnalysisFetching = useCustomSelector(scheduleAnalysesSelectors.scheduleAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [patient, setPatient] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchScheduleAnalysis = useCallback((id) => {
    setOpen(true);
    getScheduleAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { schedule_analysis } } = payload;
        handleSetData(schedule_analysis);
      }
    })
  }, [getScheduleAnalysis]);

  const handleSetData = (schedule_analysis) => {
    setResult(schedule_analysis?.result);
    setPatient(schedule_analysis?.patient);
  };

  const handleExiting = () => {
    setResult('');
    setPatient(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl54s`);
    clearLocalActiveScheduleAnalysis();
    clearLocalAnalysesEvents();

  };

  const handleOpenPatientChat = () => {
    if (patient?.id) navigate(`/patients/${patient.id}`);
  };

  const handleClose = () => setOpen(false);

  const fetchScheduleAnalysesEvents = useCallback((offset, schedule_analysis_id) => {
    getScheduleAnalysesEvents({ offset, schedule_analysis_id })
  }, [ getScheduleAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchScheduleAnalysesEvents(0, schedule_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!schedule_analysis_id) fetchScheduleAnalysis(schedule_analysis_id);
  }, [schedule_analysis_id, fetchScheduleAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { scheduleAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {schedule_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={schedule_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchScheduleAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
          {patient?.id && <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            <Button 
              variant='contained' 
              onClick={handleOpenPatientChat} 
              sx={{ m: 2 }}
            >
              Show Patient Chat
            </Button>
          </Box>}
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ScheduleAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);